import i18n from "i18next"
import { initReactI18next } from "react-i18next"

import { env } from "@/api/env"
import { STORAGE_KEYS, readStored, writeStored } from "@/lib/storage"
import { setCurrentLanguage } from "./current-language"
import enCommon from "./locales/en/common.json"
import frCommon from "./locales/fr/common.json"
import enErrors from "./locales/en/errors.json"
import frErrors from "./locales/fr/errors.json"
import enNav from "./locales/en/nav.json"
import frNav from "./locales/fr/nav.json"

export const SUPPORTED_LANGUAGES = ["fr", "en"] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

/**
 * `common`, `errors` and `nav` (the shell's menus, palette and alerts) are
 * shared namespaces (LLD §1.2): `errors` backs
 * `problemMessage()` (LLD §5.4), reachable from any screen the instant an
 * API call fails, so -- unlike a feature namespace (`withNamespace`,
 * `src/i18n/lazy-route.ts`) -- it ships in the shell rather than loading
 * lazily with a route.
 */
export const resources = {
  en: { common: enCommon, errors: enErrors, nav: enNav },
  fr: { common: frCommon, errors: frErrors, nav: frNav },
} as const

export const LANGUAGE_STORAGE_KEY = STORAGE_KEYS.language

const isSupported = (value: string | null | undefined): value is SupportedLanguage =>
  value === "en" || value === "fr"

/**
 * Stored choice first, then the browser's preference, then the configured
 * default. Hand-rolled rather than using i18next-browser-languagedetector:
 * this runs in the SHELL, the package costs ~5 KB gzipped, and the logic is
 * fifteen lines over a storage key we already own (NFR-DSH-01).
 */
export function detectLanguage(): SupportedLanguage {
  const stored = readStored(LANGUAGE_STORAGE_KEY)
  if (isSupported(stored)) return stored
  for (const candidate of navigator.languages) {
    const base = candidate.split("-")[0]
    if (isSupported(base)) return base
  }
  return env.VITE_DEFAULT_LOCALE
}

export async function initI18n(): Promise<typeof i18n> {
  await i18n.use(initReactI18next).init({
    resources,
    lng: detectLanguage(),
    fallbackLng: env.VITE_DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: "common",
    interpolation: { escapeValue: false },
  })

  // Keep the non-React accessor in step: the mutator reads it on every request
  // so server-produced Problem.detail messages arrive in the operator's
  // language (PDD §9.7).
  const sync = (lng: string) => {
    const resolved = lng.startsWith("fr") ? "fr" : "en"
    setCurrentLanguage(resolved)
    writeStored(LANGUAGE_STORAGE_KEY, resolved)
    // Screen readers pick their voice from <html lang> (WCAG 3.1.1).
    document.documentElement.lang = resolved
  }
  sync(i18n.language)
  i18n.on("languageChanged", sync)

  return i18n
}

export { i18n }
